import { store, getElement } from '@wordpress/interactivity';

import './frontend.scss';

store('itp/carousel-images', {
  callbacks: {
    initCarousel() {
      const { ref: el } = getElement();
      const wrapper = el.closest('.wp-block-itp-carousel__wrapper');

      // Prevent duplicate initialization.
      if (wrapper && !wrapper.dataset.swipeInitialized) {
        initSwipe(wrapper);
        wrapper.dataset.swipeInitialized = 'true';
      }
    },
  },
});

/**
 * Initialize swipe/drag functionality for the carousel.
 *
 * @param {HTMLElement} wrapper The carousel wrapper element.
 */
function initSwipe(wrapper) {
  const containers = wrapper.querySelectorAll('.wp-block-itp-carousel__container');

  // Guard against malformed markup.
  if (!containers.length) {
    return;
  }

  const dragThreshold = 5;

  let isDragging = false;
  let hasDragged = false;
  let isHovering = false;
  let startX = 0;
  let startTime = 0;
  let velocity = 0;
  let previousTranslate = 0;
  let currentTranslate = 0;
  let animationId = null;
  let autoScrollId = null;

  const getPositionX = (e) => {
    if (e.type.includes('mouse')) return e.pageX;
    return e.touches?.[0]?.clientX ?? 0;
  };

  const setCursor = (cursor) => {
    wrapper.style.cursor = cursor;
    containers.forEach((c) => (c.style.cursor = cursor));
  };

  const setPosition = () => {
    containers.forEach((c) => {
      c.style.transform = `translateX(${currentTranslate}px)`;
    });
  };

  // Keeps the position within bounds for infinite scroll. When we scroll past
  // one full container width, we reset back to create the looping effect.
  const normalizePosition = () => {
    const width = containers[0].offsetWidth;
    if (!width) {
      return;
    }

    while (currentTranslate <= -width) {
      currentTranslate += width;
      previousTranslate += width;
    }
    while (currentTranslate > 0) {
      currentTranslate -= width;
      previousTranslate -= width;
    }
  };

  const animate = () => {
    normalizePosition();
    setPosition();
    if (isDragging) {
      animationId = requestAnimationFrame(animate);
    }
  };

  const startAutoScroll = () => {
    // Prevent multiple auto-scroll loops from running simultaneously.
    if (autoScrollId) {
      return;
    }

    const speedInSeconds =
      parseFloat(getComputedStyle(containers[0]).getPropertyValue('--carousel-scroll-speed')) || 45;
    const containerWidth = containers[0].offsetWidth;

    // Guard against division by zero or invalid values.
    if (!containerWidth || !speedInSeconds) {
      return;
    }

    const pixelsPerSecond = containerWidth / speedInSeconds;

    let lastTime = Date.now();

    const scroll = () => {
      if (isDragging) {
        cancelAnimationFrame(autoScrollId);
        autoScrollId = null;
        return;
      }

      const now = Date.now();

      // Only scroll when not hovering. Reset lastTime to prevent jump on resume.
      if (isHovering) {
        lastTime = now;
      } else {
        const delta = (now - lastTime) / 1000;
        lastTime = now;

        currentTranslate -= pixelsPerSecond * delta;
        normalizePosition();
        setPosition();
      }

      autoScrollId = requestAnimationFrame(scroll);
    };

    autoScrollId = requestAnimationFrame(scroll);
  };

  const onStart = (e) => {
    // Only respond to left mouse button clicks.
    if (e.type === 'mousedown' && e.button !== 0) {
      return;
    }

    // Prevent default on mouse to avoid text selection and image dragging.
    if (e.type === 'mousedown') e.preventDefault();

    isDragging = true;
    hasDragged = false;
    startX = getPositionX(e);
    startTime = Date.now();
    velocity = 0;

    // Sync previousTranslate with current position to prevent jump on drag start.
    previousTranslate = currentTranslate;

    // Cancel any running animations to prevent conflicts.
    if (autoScrollId) {
      cancelAnimationFrame(autoScrollId);
      autoScrollId = null;
    }
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }

    animationId = requestAnimationFrame(animate);
    setCursor('grabbing');
  };

  const onMove = (e) => {
    if (!isDragging) {
      return;
    }

    const diff = getPositionX(e) - startX;
    currentTranslate = previousTranslate + diff;

    // Track if user has moved enough to be considered a drag vs a click.
    if (Math.abs(diff) > dragThreshold) {
      hasDragged = true;
    }

    // Calculate velocity for momentum effect on release.
    const timeDiff = Date.now() - startTime;
    if (timeDiff > 0) {
      velocity = diff / timeDiff;
    }
  };

  const onEnd = () => {
    if (!isDragging) {
      return;
    }

    isDragging = false;
    cancelAnimationFrame(animationId);
    animationId = null;

    const momentumDistance = velocity * 300;
    const startMomentum = currentTranslate;
    const startMomentumTime = Date.now();
    const duration = 500;

    // Applies momentum with cubic ease-out for natural deceleration.
    const applyMomentum = () => {
      // Stop if user starts a new drag during momentum.
      if (isDragging) {
        return;
      }

      const elapsed = Date.now() - startMomentumTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      currentTranslate = startMomentum + momentumDistance * easeOut;
      normalizePosition();
      setPosition();

      if (progress < 1) {
        requestAnimationFrame(applyMomentum);
      } else {
        previousTranslate = currentTranslate;
        startAutoScroll();
      }
    };

    // Only apply momentum if velocity is significant.
    if (Math.abs(velocity) > 0.1) {
      applyMomentum();
    } else {
      previousTranslate = currentTranslate;
      startAutoScroll();
    }

    setCursor('grab');

    // Delay reset to allow click event to be blocked if user dragged.
    setTimeout(() => (hasDragged = false), 50);
  };

  // Prevents link clicks when user was dragging rather than clicking.
  const preventClick = (e) => {
    if (hasDragged) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  };

  // Hover events for pause on hover functionality.
  wrapper.addEventListener('mouseenter', () => (isHovering = true));
  wrapper.addEventListener('mouseleave', () => (isHovering = false));

  // Touch events on wrapper.
  wrapper.addEventListener('touchstart', onStart, { passive: true });
  wrapper.addEventListener('touchmove', onMove, { passive: true });
  wrapper.addEventListener('touchend', onEnd);

  // Mouse events - mousemove/mouseup on document to track outside wrapper bounds.
  wrapper.addEventListener('mousedown', onStart);
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onEnd);

  // Use capture phase to intercept clicks before they reach child elements.
  wrapper.addEventListener('click', preventClick, true);
  wrapper.addEventListener('contextmenu', (e) => isDragging && e.preventDefault());

  // Setup containers - events and disable CSS animation.
  containers.forEach((c) => {
    c.addEventListener('mousedown', onStart);
    c.addEventListener('touchstart', onStart, { passive: true });
    c.addEventListener('click', preventClick, true);
    c.style.animation = 'none';
  });

  setCursor('grab');
  wrapper.style.userSelect = 'none';

  startAutoScroll();
}
